import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { SessionEndedError } from "./auth/sessionEnded";

/** Surface every query/mutation failure in one consistent place. */
function notifyError(error: unknown): void {
  if (error instanceof SessionEndedError) return;
  const message =
    error instanceof Error ? error.message : "Something went wrong";
  toast.error(message);
}

/**
 * Create one cache owner. AuthGate creates a fresh instance for each effective
 * subject+role set, so a late request from an old session can only repopulate
 * the old, detached client.
 */
export function createAppQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) =>
          !(error instanceof SessionEndedError) && failureCount < 1,
      },
    },
    queryCache: new QueryCache({ onError: notifyError }),
    mutationCache: new MutationCache({ onError: notifyError }),
  });
  // The dense operations UI preserves its explicit no-focus-refresh posture.
  // Analyst data is collaborative and must notice teammate-created builds and
  // newly published models when a stale tab is revisited.
  client.setQueryDefaults(["analyst"], { refetchOnWindowFocus: true });
  return client;
}
